import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { DocsThemeService, ScrollContainerService } from '../../core/services';
import { ModalService } from '../modal';
import { DocsSearch } from '../docs-search/docs-search';
import { DOCS_ENDPOINTS } from '../../pages/docs/data/docs-api-data';
import { DOCS_ANNOUNCEMENT, DocsAnnouncement } from './docs-announcement';

interface EndpointNavItem {
  id: string;
  label: string;
}

interface DocsPageLink {
  path: string;
  label: string;
}

const DOCS_PAGE_ORDER: DocsPageLink[] = [
  { path: '/docs', label: 'Introduction' },
  { path: '/docs/quickstart', label: 'Quickstart' },
  { path: '/docs/authentication', label: 'Authentication' },
  { path: '/docs/making-a-call', label: 'Making a call' },
  { path: '/docs/pricing-limits', label: 'Pricing & limits' },
  { path: '/docs/errors', label: 'Errors' },
  { path: '/docs/account-filters', label: 'Account & filters' },
  { path: '/docs/api-reference', label: 'All endpoints' },
  ...DOCS_ENDPOINTS.map(ep => ({ path: `/docs/api-reference/${ep.id}`, label: ep.id.toUpperCase() })),
  { path: '/docs/changelog', label: 'Changelog' },
];

interface TocItem {
  id: string;
  label: string;
  level: 2 | 3;
}

const TOC_THRESHOLD = 96;
const ANNOUNCEMENT_KEY = 'mila.docs.announcement.dismissed';

function readDismissed(): string | null {
  try { return localStorage.getItem(ANNOUNCEMENT_KEY); } catch { return null; }
}

@Component({
  selector: 'app-docs-layout',
  standalone: false,
  templateUrl: './docs-layout.html',
})
export class DocsLayout implements AfterViewInit, OnDestroy {
  @ViewChild('docsScroll', { static: true }) docsScrollRef!: ElementRef<HTMLElement>;
  readonly endpoints: EndpointNavItem[] = DOCS_ENDPOINTS.map(ep => ({ id: ep.id, label: ep.id.toUpperCase() }));

  expanded: Record<string, boolean> = Object.fromEntries(DOCS_ENDPOINTS.map(ep => [ep.id, true]));
  scrolled = false;

  /** Phones: the sidebar is a drawer behind the top bar's menu button
   *  (always shown, and this stays false, on desktop). */
  navOpen = false;

  openNav(): void {
    this.navOpen = true;
    // The page behind stays put while the drawer scrolls.
    document.body.classList.add('docs-nav-open');
  }

  closeNav(): void {
    if (!this.navOpen) return;
    this.navOpen = false;
    document.body.classList.remove('docs-nav-open');
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeNav();
  }

  /** The bar at the top (see docs-announcement.ts), unless this browser
   *  already closed this one. */
  announcement: DocsAnnouncement | null = DOCS_ANNOUNCEMENT && readDismissed() !== DOCS_ANNOUNCEMENT.id ? DOCS_ANNOUNCEMENT : null;

  dismissAnnouncement(): void {
    if (!this.announcement) return;
    try { localStorage.setItem(ANNOUNCEMENT_KEY, this.announcement.id); } catch { /* private mode etc. */ }
    this.announcement = null;
  }

  // ── "On this page" rail ────────────────────────────────────────────
  tocItems: TocItem[] = [];
  activeTocId: string | null = null;
  private tocHeadingEls: HTMLElement[] = [];
  private settlingTocClick = false;
  private cancelTocSettle: (() => void) | null = null;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private scrollContainer: ScrollContainerService,
    private modalService: ModalService,
    private router: Router,
    readonly docsTheme: DocsThemeService,
  ) {}

  ngAfterViewInit(): void {
    document.body.classList.add('docs-native-scroll');
    this.scrollContainer.element = document.scrollingElement as HTMLElement;
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntil(this._destroy$),
    ).subscribe(() => {
      // A link picked in the drawer lands on its page with the drawer shut.
      this.closeNav();
      this.rebuildToc();
    });
    this.rebuildToc();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.scrolled = (document.scrollingElement?.scrollTop ?? 0) > 4;
    if (!this.settlingTocClick) this.updateActiveToc();
  }
  private rebuildToc(): void {
    setTimeout(() => {
      const content = this.docsScrollRef?.nativeElement;
      if (!content) return;
      const headings = Array.from(content.querySelectorAll<HTMLElement>('.docs-h2[id], .docs-h3[id]'));
      if (headings.length > 1) {
        this.tocHeadingEls = headings;
        this.tocItems = headings.map(h => ({
          id: h.id,
          label: h.textContent?.trim() ?? '',
          level: h.classList.contains('docs-h3') ? 3 : 2,
        }));
      } else {
        this.tocHeadingEls = [];
        this.tocItems = [];
      }
      this.updateActiveToc();
    });
  }

  private updateActiveToc(): void {
    if (!this.tocHeadingEls.length) {
      this.activeTocId = null;
      return;
    }

    const scroller = document.scrollingElement;
    if (scroller && scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2) {
      this.activeTocId = this.tocHeadingEls[this.tocHeadingEls.length - 1].id;
      return;
    }
    let current = this.tocHeadingEls[0].id;
    for (const el of this.tocHeadingEls) {

      if (el.getBoundingClientRect().top <= TOC_THRESHOLD + 1) {
        current = el.id;
      } else {
        break;
      }
    }
    this.activeTocId = current;
  }

  onTocLinkClick(event: MouseEvent, id: string): void {
    event.preventDefault();
    this.cancelTocSettle?.();
    this.activeTocId = id;
    const target = this.tocHeadingEls.find(el => el.id === id);
    if (!target) return;
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
      return;
    }
    this.settlingTocClick = true;
    const onSettled = () => {
      this.settlingTocClick = false;
      this.cancelTocSettle = null;
      this.updateActiveToc();
    };
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', onSettled, { once: true });
      this.cancelTocSettle = () => window.removeEventListener('scrollend', onSettled);
    } else {
      const timer = setTimeout(onSettled, 700);
      this.cancelTocSettle = () => clearTimeout(timer);
    }
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  toggleEndpoint(id: string): void {
    this.expanded[id] = !this.expanded[id];
  }

  // ── Prev/next pagination (docs-layout.html, bottom of every page) ──────
  private get currentPageIndex(): number {
    const path = this.router.url.split('#')[0].split('?')[0];
    return DOCS_PAGE_ORDER.findIndex(p => p.path === path);
  }

  get prevPage(): DocsPageLink | null {
    const i = this.currentPageIndex;
    return i > 0 ? DOCS_PAGE_ORDER[i - 1] : null;
  }

  get nextPage(): DocsPageLink | null {
    const i = this.currentPageIndex;
    return i >= 0 && i < DOCS_PAGE_ORDER.length - 1 ? DOCS_PAGE_ORDER[i + 1] : null;
  }

  private searchOpen = false;

  @HostListener('window:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
    event.preventDefault();
    if (this.searchOpen) return; // already open don't stack a second one
    this.openSearch();
  }

  openSearch(): void {
    this.searchOpen = true;
    // Top-aligned like every command palette, so the box doesn't jump as
    // the result list grows or shrinks while typing.
    this.modalService.medium(DocsSearch, { ariaLabel: 'Search the docs', position: 'top' })
      .closed$.pipe(takeUntil(this._destroy$))
      .subscribe(() => { this.searchOpen = false; });
  }

  ngOnDestroy(): void {
    document.body.classList.remove('docs-native-scroll', 'docs-nav-open');
    this.cancelTocSettle?.();
    this._destroy$.next();
    this._destroy$.complete();
  }
}
